import RAPIER from '@dimforge/rapier3d-compat'

import { loadRobloxMeshGeometry } from '../render/loadRobloxMesh'
import { CORNER_WEDGE_HULL_POINTS, WEDGE_HULL_POINTS } from '../shapes'
import { ShapeKind } from '../types'

/** Z 周り 90° のクォータニオン。Roblox 円柱(軸=X)を rapier 円柱(軸=Y)へ合わせる。 */
const CYLINDER_AXIS_ROTATION = { x: 0, y: 0, z: Math.SQRT1_2, w: Math.SQRT1_2 }

/** 直径から半径（半長）を返す。退化を避けるため極小下限でクランプする。 */
export const safeHalf = (diameter: number): number =>
  Math.max(Number.EPSILON, diameter / 2)

/**
 * 形状に応じた rapier コライダー記述を作る。Part.Size は半径/半長/ハル点へ直接
 * 反映する（rapier はコライダーをスケールしないため）。退化時は箱で代替する。
 *
 * 球・円柱は Roblox 同様に断面を真円に保つため最小寸法を直径に使う（描画側も
 * 子メッシュを補正して一致させる）。
 */
export const colliderDescForShape = (
  shape: ShapeKind | undefined,
  size: { x: number; y: number; z: number },
): RAPIER.ColliderDesc => {
  const sx = size.x
  const sy = size.y
  const sz = size.z
  const cuboid = () => RAPIER.ColliderDesc.cuboid(sx / 2, sy / 2, sz / 2)
  switch (shape) {
    case 'ball':
      return RAPIER.ColliderDesc.ball(safeHalf(Math.min(sx, sy, sz)))
    case 'cylinder': {
      // 軸=X（長さ=size.x）、断面は真円（直径=min(size.y, size.z)）。
      const desc = RAPIER.ColliderDesc.cylinder(
        safeHalf(sx),
        safeHalf(Math.min(sy, sz)),
      )
      desc.setRotation(CYLINDER_AXIS_ROTATION)
      return desc
    }
    case 'wedge':
    case 'cornerWedge': {
      const unit =
        shape === 'wedge' ? WEDGE_HULL_POINTS : CORNER_WEDGE_HULL_POINTS
      const points = new Float32Array(unit.length)
      for (let i = 0; i < unit.length; i += 3) {
        points[i] = unit[i] * sx
        points[i + 1] = unit[i + 1] * sy
        points[i + 2] = unit[i + 2] * sz
      }
      return RAPIER.ColliderDesc.convexHull(points) ?? cuboid()
    }
    case 'box':
    default:
      return cuboid()
  }
}

/**
 * 物理用メッシュ形状（MeshPart の三角形メッシュ）。頂点は bbox を単位箱
 * （各軸 ±0.5）へ正規化済みで、コライダー生成時に Part.Size を乗算する
 * （描画側 loadRobloxMeshFor の MeshPart 正規化と同じ写像）。
 */
export interface MeshColliderSource {
  readonly vertices: Float32Array
  readonly indices: Uint32Array
}

/** URL → 取得済み物理メッシュ形状（モジュール内キャッシュ。失敗は null）。 */
const meshColliderSourceCache = new Map<
  string,
  Promise<MeshColliderSource | null>
>()

/**
 * MeshPart の .mesh を取得し、物理コライダー用の正規化三角形メッシュへ変換する。
 * 失敗時は null（呼び出し側は箱コライダーのまま運用を続ける）。
 */
export const loadMeshColliderSource = (
  url: string,
): Promise<MeshColliderSource | null> => {
  const cached = meshColliderSourceCache.get(url)
  if (cached) {
    return cached
  }
  const loading = loadRobloxMeshGeometry(url)
    .then((geometry): MeshColliderSource | null => {
      try {
        const position = geometry.getAttribute('position')
        if (!position || position.count < 3) {
          return null
        }
        // bbox を単位箱（±0.5）へ正規化する（中心合わせ + 1/extent スケール）。
        let minX = Infinity
        let minY = Infinity
        let minZ = Infinity
        let maxX = -Infinity
        let maxY = -Infinity
        let maxZ = -Infinity
        for (let i = 0; i < position.count; i += 1) {
          minX = Math.min(minX, position.getX(i))
          minY = Math.min(minY, position.getY(i))
          minZ = Math.min(minZ, position.getZ(i))
          maxX = Math.max(maxX, position.getX(i))
          maxY = Math.max(maxY, position.getY(i))
          maxZ = Math.max(maxZ, position.getZ(i))
        }
        const extentX = Math.max(0.001, maxX - minX)
        const extentY = Math.max(0.001, maxY - minY)
        const extentZ = Math.max(0.001, maxZ - minZ)
        const centerX = (minX + maxX) / 2
        const centerY = (minY + maxY) / 2
        const centerZ = (minZ + maxZ) / 2
        const vertices = new Float32Array(position.count * 3)
        for (let i = 0; i < position.count; i += 1) {
          vertices[i * 3] = (position.getX(i) - centerX) / extentX
          vertices[i * 3 + 1] = (position.getY(i) - centerY) / extentY
          vertices[i * 3 + 2] = (position.getZ(i) - centerZ) / extentZ
        }
        const index = geometry.getIndex()
        const indices =
          index ?
            Uint32Array.from(index.array)
          : Uint32Array.from({ length: position.count }, (_, i) => i)
        return { vertices, indices }
      } finally {
        geometry.dispose()
      }
    })
    .catch((error: unknown) => {
      console.warn(`物理メッシュの読込に失敗しました (${url}):`, error)
      // 失敗は永続キャッシュしない（次のシーン読込等で再試行できるように）。
      meshColliderSourceCache.delete(url)
      return null
    })
  meshColliderSourceCache.set(url, loading)
  return loading
}

/**
 * trimesh コライダーを適用する MeshPart の最小寸法（最大軸、studs）。
 * 箱コライダーとの差がアバターの「乗る・ぶつかる」体感に影響する大きさに
 * 限定する（小物まで対象にするとメッシュの多いシーンで生成コストと
 * メモリが嵩む。例: RJS02 は静的 MeshPart が 1000 個超）。
 */
export const MESH_COLLIDER_MIN_EXTENT = 8

/**
 * 正規化メッシュ（単位箱）を Part.Size へスケールした trimesh コライダー記述。
 * 生成に失敗した場合は null（呼び出し側が箱へフォールバック）。
 */
export const trimeshColliderDesc = (
  source: MeshColliderSource,
  size: { x: number; y: number; z: number },
): RAPIER.ColliderDesc | null => {
  const scaled = new Float32Array(source.vertices.length)
  for (let i = 0; i < source.vertices.length; i += 3) {
    scaled[i] = source.vertices[i] * size.x
    scaled[i + 1] = source.vertices[i + 1] * size.y
    scaled[i + 2] = source.vertices[i + 2] * size.z
  }
  try {
    return RAPIER.ColliderDesc.trimesh(scaled, source.indices)
  } catch (error) {
    console.warn('trimesh コライダーの生成に失敗しました:', error)
    return null
  }
}
